import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { ShareIcon } from '@heroicons/react/24/outline';
import { useQueries } from '@tanstack/react-query';
import type { ApiReconstructedDocumentDetail, DocumentProjectSummary } from '@avisoma/shared';
import { Button } from '../../catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../catalyst/dropdown';
import backend from '../../services/backend';
import { formatShortId } from '../ID';

const graphHref = (projectId: string, reconstructedDocumentId: string) =>
  `/projects/${encodeURIComponent(projectId)}/graph?${new URLSearchParams({ node: `doc:${reconstructedDocumentId}` })}`;

/**
 * Opens the project graph with this document selected. The document belongs to every project any of its source
 * documents is linked to: none shows nothing, one is a button, more are a menu.
 */
export function ExploreInGraph({ entity }: { entity: ApiReconstructedDocumentDetail }) {
  const documentIds = [...new Set(entity.sourceDocuments.map((d) => d.documentId))];
  // The same query key as `DocumentProjects`, so the two share the cache.
  const projects = useQueries({
    queries: documentIds.map((documentId) => ({
      queryKey: ['documentProjects', documentId],
      queryFn: () => backend.getDocumentProjects(documentId),
    })),
    // Nothing until every query settled and none failed: a partial list would show one project's button first.
    combine: (results) => {
      if (results.some((r) => r.isPending || r.isError)) return [];
      const byId = new Map<string, DocumentProjectSummary>();
      for (const project of results.flatMap((r) => r.data?.data.projects ?? [])) {
        if (!byId.has(project.id)) byId.set(project.id, project);
      }
      return [...byId.values()];
    },
  });

  if (projects.length === 0) return null;

  if (projects.length === 1) {
    return (
      <Button outline href={graphHref(projects[0].id, entity.id)} title="Open the project graph with this document selected">
        <ShareIcon className="size-4" />
        Explore in graph
      </Button>
    );
  }

  return (
    <Dropdown>
      <DropdownButton as={Button} outline title="Open a project graph with this document selected">
        <ShareIcon className="size-4" />
        Explore in graph
        <ChevronDownIcon />
      </DropdownButton>
      <DropdownMenu anchor="bottom end">
        {projects.map((project) => {
          const esn = project.data?.projectData?.esn;
          return (
            <DropdownItem key={project.id} href={graphHref(project.id, entity.id)}>
              <DropdownLabel>Project {formatShortId(project.id)}</DropdownLabel>
              {esn && <DropdownDescription>ESN {esn}</DropdownDescription>}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
}
